import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconShare } from "@tabler/icons-react";
import { z } from "zod";

import { OnboardingInvitationAccess } from "@components/OnboardingInvitationAccess";

import { useInviteDoctor } from "@hooks/mutations/onboarding/useInviteDoctor";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";

import { OnboardingInvitationCredentials } from "@customTypes/accountManagement";
import { useAuth } from "@contexts/auth";

interface Inputs {
  identifier: string;
  partner_id?: string;
}

export const OnboardingInviteModal = () => {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canManageAccess = can("access.manage");
  const [opened, { open, close }] = useDisclosure(false);
  const [invitation, setInvitation] =
    useState<OnboardingInvitationCredentials | null>(null);

  const { data: partnersData } = useGetPartners({ isActive: true, isEnabled: canManageAccess });
  const partnersOptions = (partnersData || []).map(partner => ({
    label: partner.name,
    value: partner._id,
  }));

  const schema = z.object({
    identifier: z.string().min(2, t("onboarding-social-handle-required")),
    partner_id: z.string().optional(),
  });

  const form = useForm<Inputs>({
    mode: "uncontrolled",
    validate: zodResolver(schema),
    initialValues: {
      identifier: "",
      partner_id: "",
    },
  });

  const { mutate: inviteDoctor, isLoading } = useInviteDoctor();

  const handleSubmit = (values: Inputs) => {
    inviteDoctor(
      {
        identifier: values.identifier,
        roles: ["doctor"],
        partner_id: canManageAccess ? values.partner_id || undefined : undefined,
      },
      {
        onSuccess: res => {
          setInvitation(res.data);
        },
      }
    );
  };

  const handleClose = () => {
    close();
    setTimeout(() => {
      form.reset();
      setInvitation(null);
    }, 300);
  };

  return (
    <>
      <Button variant="outline" onClick={open} leftSection={<IconShare size={16} />}>
        {t("onboarding-generate-link")}
      </Button>

      <Modal opened={opened} onClose={handleClose} title={<Text fw={600}>{t("onboarding-generate-doctor-link")}</Text>}>
        {!invitation ? (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                {t("onboarding-invite-desc")}
              </Text>
              
              <TextInput
                label={t("onboarding-social-handle")}
                description={t("onboarding-social-handle-desc")}
                placeholder={t("onboarding-social-handle-placeholder")}
                withAsterisk
                key={form.key("identifier")}
                {...form.getInputProps("identifier")}
              />

              {canManageAccess && <Select
                label={t("onboarding-assign-partner")}
                placeholder={t("onboarding-select-partner")}
                data={partnersOptions}
                searchable
                clearable
                key={form.key("partner_id")}
                {...form.getInputProps("partner_id")}
              />}

              <Button type="submit" loading={isLoading} fullWidth mt="md">
                {t("onboarding-generate-link")}
              </Button>
            </Stack>
          </form>
        ) : (
          <OnboardingInvitationAccess
            invitation={invitation}
            showSuccessHeader
            onDone={handleClose}
          />
        )}
      </Modal>
    </>
  );
};
